
/*!
 * Quiz Results – compact (no upsells, no checkout button)
 * - Compact card for foundation
 * - "Add to Bag" under the FIRST foundation card (robust variant resolver + price/lock state)
 * - Cart is cleared once right after quiz completion, before rendering results
 *
 * Dependencies: none
 */
(function () {
  /* ============================== CONFIG =============================== */
  const ROOT_SELECTOR = '.oct-quiz-product-block';
  const ITEM_SELECTOR = '.oct-quiz-products__item';
  const ROOT_FALLBACKS = ['.oct-quiz-results', '[data-oct-quiz-root]', 'main', 'body'];
  const ITEM_FALLBACKS = ['.oct-quiz-result-item', '.oct-quiz-result', '.oct-quiz-card', '[data-oct-result-item]'];
  const CTA_WRAPPER_SELECTOR = '.oct-quiz-result-actions-wrapper';
  const CTA_BTN_SELECTOR = `${CTA_WRAPPER_SELECTOR} .oct-quiz-btn`;
  const SHOP_ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  /* no headline under the button */
  const HEADLINE_HTML = ``;

  /* =============================== UTILS ================================ */
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const tidy = (s) => String(s || '').trim();
  const $ = (sel, scope = document) => scope.querySelector(sel);

  function pickFirst(selectors, scope = document) {
    for (const s of selectors) {
      const el = scope.querySelector(s);
      if (el) return el;
    }
    return null;
  }

  async function fetchCartJson() {
    try {
      const r = await fetch(`${SHOP_ROOT}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
      return await r.json();
    } catch { return null; }
  }

  async function cartHasVariant(variantId) {
    try {
      const cart = await fetchCartJson();
      const vid = Number(variantId || 0);
      if (!vid || !cart || !Array.isArray(cart.items)) return false;
      return cart.items.some(it => Number(it.id) === vid);
    } catch { return false; }
  }

  /* fire "cart:updated" after any cart mutation (fetch/XHR) */
  (function hookCartNetworkOnce(){
    if (window.__cartHooked) return; window.__cartHooked = true;

    const isCartMutation = (urlLike) => {
      try {
        const u = new URL(urlLike, location.origin);
        return u.pathname.startsWith(`${SHOP_ROOT}cart/`) && /(?:add|change|update|clear)\.js$/i.test(u.pathname);
      } catch { return false; }
    };

    const origFetch = window.fetch;
    window.fetch = async function(...args){
      const res = await origFetch.apply(this, args);
      if (args[0] && isCartMutation(args[0])) {
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
      return res;
    };

    const XHR = window.XMLHttpRequest;
    const open = XHR.prototype.open;
    const send = XHR.prototype.send;
    XHR.prototype.open = function(method, url, ...rest){
      this.__octCartURL = url;
      return open.call(this, method, url, ...rest);
    };
    XHR.prototype.send = function(...args){
      this.addEventListener('load', () => {
        if (this.__octCartURL && isCartMutation(this.__octCartURL)) {
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }
      });
      return send.apply(this, args);
    };
  })();

  function getBgImageUrlFromEl(el) {
    if (!el) return '';
    const inline = el.style && el.style.backgroundImage;
    const comp = getComputedStyle(el).backgroundImage;
    const v = inline || comp || '';
    if (!v || v === 'none') return '';
    const m = String(v).match(/url\((['"]?)(.*?)\1\)/i);
    return m ? m[2] : '';
  }

  function variantFromTitleText(titleText) {
    const t = tidy(titleText);
    if (!t) return '';
    const i = t.lastIndexOf('/');
    return i >= 0 ? tidy(t.slice(i + 1)) : '';
  }

  function stripVariantFromTitle(title, variantName) {
    let t = tidy(title);
    if (!t) return t;
    if (t.includes('/')) t = t.split('/').slice(0, -1).join('/').trim();
    if (variantName) {
      const esc = variantName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      t = t.replace(new RegExp(`\\s*[-|–|\\|]\\s*${esc}$`, 'i'), '').trim();
    }
    return t;
  }

  function readSwatchColor(item) {
    const span = item.querySelector('.swatch-detail .swatch');
    if (!span) return '';
    const inline = span.getAttribute('style') || '';
    const m = inline && inline.match ? inline.match(/background(?:-color)?:\s*([^;]+)/i) : null;
    return m ? m[1].trim() : '';
  }

  function readMetaTexts(item) {
    const nodes = Array.from(item.querySelectorAll('.oct-quiz-result-item__metafields'));
    const nonSwatch = nodes
      .filter((n) => !n.querySelector('.swatch-detail'))
      .map((n) => tidy(n.textContent))
      .filter(Boolean);
    const shadeDesc = nonSwatch[0] || '';
    const undertone = nonSwatch[1] || '';
    return { shadeDesc, undertone };
  }

  function fmtMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || (window.Shopify && Shopify.currency) || 'USD' }).format((Number(cents || 0)) / 100);
    } catch { return `$${(Number(cents || 0) / 100).toFixed(2)}`; }
  }

  /* =============== PRICE META (для цены на кнопке) ===================== */
const priceCache = new Map();

async function getVariantMeta(variantId) {
  const key = String(variantId);
  if (priceCache.has(key)) return priceCache.get(key);
  try {
    const r = await fetch(`${SHOP_ROOT}variants/${variantId}.js`, {
      credentials: 'same-origin',
      cache: 'no-store'
    });
    const v = await r.json();
    const meta = {
      price: Number(v?.price || 0),
      compare_at_price: Number(v?.compare_at_price || 0),
      currency: (window.Shopify && Shopify.currency) || 'USD',
      featured_image: v?.featured_image || null
    };
    priceCache.set(key, meta);
    return meta;
  } catch {
    return { price: 0, compare_at_price: 0, currency: 'USD', featured_image: null };
  }
}


  /* =========================== CART HELPERS ============================ */
  async function clearCart() {
    try {
      const r = await fetch(`${SHOP_ROOT}cart/clear.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      return r.ok;
    } catch { return false; }
  }

  async function waitCartEmpty(maxMs = 4000) {
    const started = Date.now();
    while (Date.now() - started < maxMs) {
      try {
        const res = await fetch(`${SHOP_ROOT}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
        const cart = await res.json();
        if (!cart?.item_count) return true;
      } catch {}
      await wait(150);
    }
    return false;
  }

  async function addItems(items) {
    const payload = {
      items: items.map((i) => ({
        id: i.id,
        quantity: i.quantity || 1,
        properties: i.properties || undefined,
        selling_plan: i.selling_plan || undefined,
      })),
    };
    const res = await fetch(`${SHOP_ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify(payload),
    });
    const txt = await res.text();
    let json = txt; try { json = JSON.parse(txt); } catch {}
    if (!res.ok) {
      console.error('[cart add failed ' + res.status + ']:', json);
      throw new Error('cart add failed ' + res.status);
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    return json;
  }

  /* =================== VARIANT RESOLUTION (ROBUST) =================== */
  function readVariantIdFromItem(item) {
    const direct =
      item?.getAttribute('data-variant-id') ||
      item?.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') ||
      item?.querySelector('[data-variant]')?.getAttribute('data-variant');
    if (direct && /^\d+$/.test(direct)) return +direct;

    const btn = item?.querySelector(`${CTA_WRAPPER_SELECTOR} .oct-quiz-btn, [data-variant-id], [data-variant]`);
    const ds = btn?.dataset?.variantId || btn?.dataset?.variant || '';
    if (ds && /^\d+$/.test(ds)) return +ds;

    const hrefNode = item?.querySelector(`${CTA_WRAPPER_SELECTOR} a[href], a.oct-quiz-btn[href], [data-variant-href]`);
    const rawHref = hrefNode?.getAttribute?.('href') || hrefNode?.getAttribute?.('data-variant-href') || '';
    if (rawHref) {
      try {
        const u = new URL(rawHref, location.origin);
        const qId = u.searchParams.get('id') || u.searchParams.get('variant') || '';
        if (qId && /^\d+$/.test(qId)) return +qId;
      } catch {}
      const m = String(rawHref).match(/[?&](?:id|variant)=(\d+)/i);
      if (m) return +m[1];
    }

    const form = item?.querySelector('form[action*="/cart/add"]');
    const formId = form?.querySelector('input[name="id"], input[name^="id"]')?.value;
    if (formId && /^\d+$/.test(formId)) return +formId;
    return null;
  }

  async function getFoundationVariantId(fallbackItem) {
  let vid = readVariantIdFromItem(fallbackItem || null);
  if (vid) return vid;

  const root = document.querySelector(ROOT_SELECTOR) || pickFirst(ROOT_FALLBACKS) || document;

  const dataNode = root.querySelector('[data-variant-id],[data-variant]');
  const dv = dataNode?.dataset?.variantId || dataNode?.dataset?.variant || '';
  if (dv && /^\d+$/.test(dv)) return +dv;

  const anyFormId = root.querySelector('form[action*="/cart/add"] input[name="id"], input[name^="id"]')?.value;
  if (anyFormId && /^\d+$/.test(anyFormId)) return +anyFormId;

  const hrefNode =
    root.querySelector(`${CTA_WRAPPER_SELECTOR} a[href]`) ||
    root.querySelector('a.oct-quiz-btn[href]') || null;
  const rawHref = hrefNode?.getAttribute?.('href') || '';
  if (rawHref) {
    try {
      const u = new URL(rawHref, location.origin);
      const qId = u.searchParams.get('id') || u.searchParams.get('variant') || '';
      if (qId && /^\d+$/.test(qId)) return +qId;
    } catch {}
    const mm = String(rawHref).match(/[?&](?:id|variant)=(\d+)/i);
    if (mm) return +mm[1];
  }

  try {
    const scope = fallbackItem || root.querySelector(ITEM_SELECTOR) || pickFirst(ITEM_FALLBACKS, root);
    const titleEl = scope?.querySelector('.oct-quiz-result-item__title') || root.querySelector('.oct-quiz-result-item__title');
    const fullTitleText = (titleEl?.textContent || '').trim();
    const wantTitle = variantFromTitleText(fullTitleText);

    const productUrl = titleEl?.getAttribute?.('href') || '';

    const vidByTitle = await resolveVariantIdFromProductJson(productUrl, wantTitle);
    if (vidByTitle) return vidByTitle;
  } catch {}

  try {
    const u = new URL(location.href);
    const q = u.searchParams.get('variant') || u.searchParams.get('id');
    if (q && /^\d+$/.test(q)) return +q;
  } catch {}

  return null;
}


  async function resolveVariantIdFromProductJson(productUrl, wantTitle) {
  if (!productUrl) return null;
  const norm = s => String(s || '').trim().replace(/\s+/g,' ').toLowerCase();
  try {
    const u = new URL(productUrl, location.origin);
    if (!/\/products\//.test(u.pathname)) return null;
    const jsonUrl = u.pathname.replace(/\/$/, '') + '.js';
    const res = await fetch(jsonUrl, { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) return null;
    const product = await res.json();

    if (wantTitle) {
      const wt = norm(wantTitle);
      const m = (product.variants || []).find(v => norm(v.title) === wt);
      if (m?.id) return Number(m.id);
    }

    return null;
  } catch {
    return null;
  }
}

  /* ======================= COMPACT RESULT CARD ======================= */
  async function buildCompactCard(item) {
    if (!item || item.dataset.mobileCardBuilt === '1') return;

    const titleLink = item.querySelector('.oct-quiz-result-item__title');
    const srcImgEl =
      item.querySelector('.oct-quiz-result-item__img, .oct-quiz-result-item__img-wrapper [style*="background-image"]') || null;
    const nativeCta = item.querySelector(CTA_BTN_SELECTOR); // native Add to cart 
    const fullTitleText = (titleLink?.textContent || '').trim();
    const variantTitle = variantFromTitleText(fullTitleText);
    const productTitle = stripVariantFromTitle(fullTitleText, variantTitle);

    const { shadeDesc, undertone } = readMetaTexts(item);
    const swatchBg = readSwatchColor(item);

    const answerImg = ''; // no upsell/answer art swap
    const quizImg = getBgImageUrlFromEl(srcImgEl);
    const imgUrl = answerImg || quizImg;

    const card = document.createElement('div');
    card.className = 'oct-result-card';

    // helpers for solid color tile preview
    function toHex(c) {
      if (!c) return null;
      const s = String(c).trim();
      if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s)) {
        const hex = s.replace('#','');
        return hex.length === 3 ? s.replace('#','').split('').map(ch=>ch+ch).join('') : hex;
      }
      const m = s.match(/rgba?\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
      if (m) {
        const [r,g,b] = m.slice(1,4).map(n=>Math.max(0,Math.min(255,parseInt(n,10))));
        const h = x => x.toString(16).padStart(2,'0');
        return `${h(r)}${h(g)}${h(b)}`;
      }
      return null;
    }
    function solidSvgDataUrl(hexNoHash) {
      const hex = hexNoHash || 'eeeeee';
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="117" height="117"><rect width="100%" height="100%" rx="8" ry="8" fill="#${hex}"/></svg>`;
      return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
    }

    const row = document.createElement('div');
    row.className = 'oct-result-row';

    const tile = document.createElement('div');
    tile.className = 'oct-swatch-tile';
    const tileImg = document.createElement('img');
    tileImg.className = 'oct-swatch-tile-img';
    const hex = toHex(swatchBg);
    tileImg.src = solidSvgDataUrl(hex);
    tileImg.alt = variantTitle || 'Shade';
    tile.appendChild(tileImg);

    const packWrap = document.createElement('div');
    packWrap.className = 'oct-swatch-pack';
    const packImg = document.createElement('img');
    packImg.className = 'oct-swatch-pack-img';
    // if (imgUrl) packImg.src = imgUrl;
    packImg.src = 'https://cdn.shopify.com/s/files/1/0577/8879/2890/files/Nexara_SilkVeil_Cushion_Foundation.png?v=1757839140'
    packImg.alt = productTitle || 'Product';
    packWrap.appendChild(packImg);
    tile.appendChild(packWrap);

    row.appendChild(tile);

    const right = document.createElement('div');
    right.className = 'oct-result-right';

    const title = document.createElement(titleLink ? 'a' : 'div');
    title.className = 'oct-result-title';
    title.textContent = productTitle || fullTitleText;
    if (titleLink) {
      title.setAttribute('href', titleLink.getAttribute('href') || '#');
      title.setAttribute('target', '_blank');
      title.setAttribute('rel', 'noopener');
      title.setAttribute('tabindex', '-1');
    }
    right.appendChild(title);

    const vTitle = document.createElement('div');
    vTitle.className = 'oct-variant-title';
    vTitle.textContent = variantFromTitleText(fullTitleText);
    const vDesc = document.createElement('div');
    vDesc.className = 'oct-variant-desc';
    vDesc.textContent = shadeDesc || undertone || '';

    right.appendChild(vTitle);
    right.appendChild(vDesc);

    row.appendChild(right);
    card.appendChild(row);

    const descHtml = item.querySelector('.oct-quiz-result-item__description')?.innerHTML || '';
    if (descHtml) {
      const long = document.createElement('div');
      long.className = 'oct-result-desc';
      long.innerHTML = descHtml;
      card.appendChild(long);
    }
    item.appendChild(card);

    /* === NO CHECKOUT BUTTON ===*/
    if (nativeCta) nativeCta.style.display = 'none';

    // смонтировать "Add to Bag" под первой картой
    mountAddToBagUnderFirst();
    item.dataset.mobileCardBuilt = '1';
  }

  /* ===== “Add to Bag” UNDER FIRST FOUNDATION CARD (no headline) ===== */
  async function mountAddToBagUnderFirst() {
    const first = document.querySelector(ITEM_SELECTOR) || pickFirst(ITEM_FALLBACKS) || null;
    if (!first || first.dataset.octAddBagMounted === '1') return;

    const actionsWrap = document.createElement('div');
    actionsWrap.className = 'oct-actions-wrap';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'oct-add-to-bag oct-btn-black';
    btn.textContent = 'ADD TO BAG'; // price appended later

    if (HEADLINE_HTML && HEADLINE_HTML.trim()) {
      const head = document.createElement('div');
      head.className = 'oct-headline-host';
      head.innerHTML = HEADLINE_HTML;
      actionsWrap.appendChild(head);
    }

    const card = first.querySelector('.oct-result-card') || first;
    actionsWrap.appendChild(btn);
    card.insertAdjacentElement('afterend', actionsWrap);
    first.dataset.octAddBagMounted = '1';

    // --- State sync: show price AND lock if in cart already ---
    async function syncBtnState() {
      const vid = await getFoundationVariantId(first);
      if (!vid) { btn.textContent = 'ADD TO BAG'; btn.disabled = true; return; }

      const meta = await getVariantMeta(vid);
      const already = await cartHasVariant(vid);

      if (already) {
        btn.textContent = `IN BAG — ${fmtMoney(meta.price, meta.currency)}`;
        btn.disabled = true;
      } else {
        btn.textContent = `ADD TO BAG — ${fmtMoney(meta.price, meta.currency)}`;
        btn.disabled = false;
      }
    }

    await syncBtnState();
    document.addEventListener('cart:updated', syncBtnState);

    btn.addEventListener('click', async (e) => {
      e.preventDefault(); e.stopPropagation();
      try {
        const vid = await getFoundationVariantId(first);
        if (!vid) return;
        if (await cartHasVariant(vid)) { await syncBtnState(); return; }

        btn.disabled = true;
        btn.textContent = 'ADDING…';
        await addItems([{ id: Number(vid), quantity: 1 }]);
        // cart:updated → syncBtnState()
      } catch (err) {
        console.warn('[Add to Bag error]', err);
      }
    });
  }

  /* ============================ HYDRATION FLOW ============================ */
  async function hydrateAll(scope) {
    const root = (scope || document).querySelector(ROOT_SELECTOR) || pickFirst(ROOT_FALLBACKS);
    if (!root) return;
    if (!root.classList.contains('oct-results--compact')) root.classList.add('oct-results--compact');

    const items = root.querySelectorAll(ITEM_SELECTOR).length
      ? root.querySelectorAll(ITEM_SELECTOR)
      : root.querySelectorAll(ITEM_FALLBACKS.join(','));

    items.forEach(buildCompactCard);
  }

  let clearedOnce = false;
  document.addEventListener('octane.quiz.completed', async () => {
    if (!clearedOnce) {
      clearedOnce = true;
      try { await clearCart(); await waitCartEmpty(); } catch {}
    }
    setTimeout(() => hydrateAll(document), 0);
  }, { passive: true });

  document.addEventListener('octane.quiz.rendered',  () => setTimeout(() => hydrateAll(document), 0), { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hydrateAll(document), { passive: true });
  } else { hydrateAll(document); }

  const mo = new MutationObserver(() => {
    hydrateAll(document);
    mountAddToBagUnderFirst();
  });
  const startObserve = () => {
    const root = document.querySelector(ROOT_SELECTOR) || pickFirst(ROOT_FALLBACKS) || document.body;
    if (root) mo.observe(root, { childList: true, subtree: true });
  };
  startObserve();
  document.addEventListener('octane.quiz.rendered', startObserve, { passive: true });

  /* =============================== STYLES ================================ */
  (function injectStylesOnce() {
    if (document.getElementById('quiz-results-styles')) return;
    const css = `
/* ---------- Foundation card ---------- */
.oct-result-card{border:1px solid #e6e0db;border-radius:10px;padding:14px;background:#fffaf7}
.oct-result-title{font-weight:600;margin-bottom:6px}
.oct-result-row{display:grid;grid-template-columns:auto 1fr;gap:12px;align-items:center}
.oct-swatch-tile{width:140px;height:140px;border-radius:8px;background:var(--swatch-bg,#f0e8e2);display:grid;place-items:center;overflow:hidden}
.oct-swatch-pack{width:100%;height:100%;background-size:cover;background-position:center;border-radius:8px}
.oct-variant-title{font-weight:600}
.oct-variant-desc{font-size:13px;opacity:.85;margin-top:2px}
.oct-result-desc{margin-top:10px;font-size:14px;opacity:.9}

/* ---------- CTA ---------- */
.oct-btn-black{
  display:block;
  width:100%;
  box-sizing:border-box;
  padding:14px 18px;
  background:#000;
  color:#fff;
  border:none;
  border-radius:0;
  text-align:center;
  letter-spacing:.08em;
  text-transform:uppercase;
  font-weight:700;
  cursor:pointer;
}
.oct-btn-black:disabled{opacity:.6;cursor:not-allowed}

.oct-add-to-bag.oct-btn-black{width:100%;margin-top:12px}
.oct-actions-wrap{max-width:488px;width:100%;display:block !important;clear:both}

/* (headline removed by default; styles kept for optional future use) */
.oct-headline-host{display:block;margin-top:10px}
.oct-results-headline{text-align:center;font-weight:800;font-size:26px;line-height:1.2;letter-spacing:.02em;color:#111;margin:0}
.oct-results-headline a{color:inherit;text-decoration:none}
`;
    const style = document.createElement('style');
    style.id = 'quiz-results-styles';
    style.textContent = css;
    document.head.appendChild(style);
  })();
})();